import { filterIpAddresses } from './ipAddressHelpers';
import { isIPv6, isIPv6CIDR } from './ipAddressValidations';
import { convertIpTo32BitInt } from './searchIPv4';

export type IPv6 = string & { readonly __brand: unique symbol };
export type IPv6CIDR = string & { readonly __brand: unique symbol };

const isValidIPv6 = (input: string): input is IPv6 => {
  return isIPv6(input);
};

const isValidIPv6CIDR = (input: string): input is IPv6CIDR => {
  return isIPv6CIDR(input);
};

const parseCIDR = (cidrIp: IPv6CIDR): [ip: IPv6, prefix: number] => {
  if (!isValidIPv6CIDR(cidrIp)) {
    throw new Error('Invalid CIDR');
  }
  const [ip, prefix] = cidrIp.split('/') as [IPv6, string];
  return [ip, Number(prefix)];
};

const convertPureIPv6To128BigInt = (ip: IPv6): bigint => {
  let sections = ip.split(':');

  const emptyIndex = sections.indexOf('');
  if (emptyIndex !== -1) {
    const missingZeros = 8 - (sections.length - 1);
    sections = [
      ...sections.slice(0, emptyIndex),
      ...Array(missingZeros).fill('0'),
      ...sections.slice(emptyIndex + 1),
    ];
  }

  return sections
    .map((num) => BigInt(`0x${num || '0'}`))
    .reduce((acc, num) => acc * BigInt(65536) + num, BigInt(0));
};

const convertEmbeddedIPv4To128BigInt = (ip: IPv6): bigint => {
  const parts = ip.split(':');
  const lastPart = parts.pop() || '';

  const ipv4AsBigInt = BigInt(convertIpTo32BitInt(lastPart));

  const ipv6Sections = parts.map((num) => BigInt(`0x${num || '0'}`));

  while (ipv6Sections.length < 6) {
    ipv6Sections.unshift(BigInt(0));
  }

  return (
    ipv6Sections.reduce((acc, num) => acc * BigInt(65536) + num, BigInt(0)) *
      BigInt(0x100000000) +
    ipv4AsBigInt
  );
};

export const convertIpTo128BitInt = (ip: IPv6): bigint => {
  return ip.includes('.')
    ? convertEmbeddedIPv4To128BigInt(ip)
    : convertPureIPv6To128BigInt(ip);
};

const isSubnetWithinParent = (subnet: IPv6CIDR, parent: IPv6CIDR): boolean => {
  const [subnetIP, subnetPrefix] = parseCIDR(subnet);
  const [parentIP, parentPrefix] = parent.split('/') as [IPv6, string];

  const subnetBin = convertIpTo128BitInt(subnetIP);
  const parentBin = convertIpTo128BitInt(parentIP);
  const parentPrefixNum = Number(parentPrefix);
  const subnetPrefixNum = Number(subnetPrefix);

  const parentMask = BigInt(128 - parentPrefixNum);

  return (
    subnetPrefixNum >= parentPrefixNum &&
    subnetBin >> parentMask === parentBin >> parentMask
  );
};

const filterIPv6CIDRs = (query: IPv6CIDR, ipList: string[]): string[] => {
  const [queryIP] = parseCIDR(query);
  const queryBigInt = convertIpTo128BitInt(queryIP);

  return ipList.filter((item) => {
    if (isValidIPv6CIDR(item)) {
      const [itemIP] = parseCIDR(item);
      return (
        convertIpTo128BitInt(itemIP) === queryBigInt ||
        isSubnetWithinParent(query, item)
      );
    }
    return isValidIPv6(item) && convertIpTo128BitInt(item) === queryBigInt;
  });
};

const filterIPv6 = (query: IPv6, ipList: string[]): string[] => {
  const queryBigInt = convertIpTo128BitInt(query);
  return ipList.filter((item) => {
    if (isValidIPv6CIDR(item)) {
      const [itemIP] = parseCIDR(item);
      return (
        convertIpTo128BitInt(itemIP) === queryBigInt ||
        isSubnetWithinParent(`${query}/128` as IPv6CIDR, item)
      );
    }
    return isValidIPv6(item) && convertIpTo128BitInt(item) === queryBigInt;
  });
};

export const filterIPv6IPs = (query: string, ipList: string[]): string[] => {
  if (isValidIPv6CIDR(query)) {
    return filterIPv6CIDRs(query, ipList);
  }

  if (isValidIPv6(query)) {
    return filterIPv6(query, ipList);
  }

  return filterIpAddresses(query, ipList);
};
